import { memo } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import cx from 'classnames';
import Logo from '@assets/images/kohler_logo.svg';
import PropTypes from 'prop-types';
import Copyright from '@components/copyright';
import FooterLinks from './footerLinks';
import NewsletterSignUp from './newsletterSignUp';

/**
 * @class
 * @param {Object} footerData footer component data with Navitems
 *
 * @returns Footer Component
 */

const Footer = ({
  primaryNavFooterData,
  secondaryNavFooterData,
  copyright
}) => {
  const footerNavItem = cx('m-0', 'text-sm', 'leading-5');

  const copyrightClass = cx(
    'p-0',
    'pt-[30px]',
    'pb-0',
    'text-sm',
    'leading-5',
    'lg:p-0',
    'xl:p-0',
    'ml-[18px]',
    'md:ml-8',
    'lg:mt-5',
    'lg:mb-[0px]',
    'lg:ml-[26px]',
    'xl:m-0',
    'xl:ml-[50px]',
    'xl:my-[25px]',
    'xl:mr-[50px]'
  );

  const socialIconsList = primaryNavFooterData.filter(d =>
    d.fields.hasOwnProperty('icon')
  );
  const socialIcons = socialIconsList?.map(i => {
    return (
      <li key={i.sys.id}>
        <a
          href={i.fields.url}
          hrefLang={i.sys.locale.split('-')[0]}
          aria-label={i.fields.ariaLabel}
          target={i.fields.urlOpenerType === 'New Tab' ? '_blank' : '_self'}
          rel='noreferrer'
        >
          <Image
            className='max-h-4 w-[22px] '
            width='48'
            height='48'
            alt={i?.fields?.icon?.fields?.altText}
            priority={true}
            src={i?.fields?.icon?.fields?.asset[0]?.fields?.file?.url}
          />
        </a>
      </li>
    );
  });

  return (
    <footer data-testid='footer'>
      <div className='flex flex-col bg-neutrals-gray100'>
        <div className='flex flex-col w-full gap-[30px] lg:pl-[32px] lg:flex-row lg:gap-0 xl:gap-0 pl-[18px] md:pl-8 lg:pb-[60px] pt-10 pb-[30px] xl:pt-20 lg:px-12 xl:pl-[50px] xl:pb-[60px]'>
          <Link href={'/'} legacyBehavior>
            <a className='flex flex-row w-100 lg:w-3/5 xl:w-3/5'>
              <Image
                className='h-auto outline-none mr-2'
                height='auto'
                width='96'
                src={Logo}
                alt='Kohler'
                priority={true}
              />
              <div className='border-l-[3px] mt-[4px] pl-2 border-l-primary border-solid h-[22px]'></div>
              <div className='flex text-black bg-transparent align-middle lg:mr-3'>
                <span className='font-helNowLight text-[20px] font-bold border-0 sm:text-[20px] '>
                  Generators
                </span>
              </div>
            </a>
          </Link>
          <div
            className='flex flex-row lg:w-full xl:justify-start lg:justify-end -ml-1.5 lg:ml-0 xl:m-0 xl:ml-0 lg:pt-0 xl:pt-0 gap-[30px] w-100 xl:w-2/5'
            role='list'
            aria-busy='true'
          >
            <ul className='flex flex-row gap-[30px]'>{socialIcons}</ul>
          </div>
        </div>
        <div className='flex flex-col xl:flex-row lg:mb-[100px] lg:mx-[32px] xl:mx-[50px]'>
          <FooterLinks data={primaryNavFooterData} />
          <NewsletterSignUp />
        </div>
      </div>
      <div className='flex flex-col font-Roman text-white xl:flex-row w-100 bg-[#000000] xl:justify-between'>
        <div className={copyrightClass}>
          <Copyright text={copyright} />
        </div>
        <div
          className='flex flex-col pb-[30px] pt-[30px] gap-5 ml-[18px] md:ml-8 lg:p-0 lg:flex-row lg:my-[25px] lg:ml-[26px] lg:pr-[30px] xl:m-0 xl:my-[25px] xl:px-12 xl:pr-10'
          role='list'
          aria-busy='true'
        >
          {secondaryNavFooterData?.map(i => {
            const { fields: item, sys } = i;
            if (item?.label !== 'Cookie Settings') {
              return (
                <a
                  key={sys?.id}
                  className={footerNavItem}
                  href={item?.url}
                  target={
                    item?.urlOpenerType === 'New Tab' ? '_blank' : '_self'
                  }
                  aria-label={item?.ariaLabel}
                  hrefLang={sys.locale.split('-')[0]}
                  rel='noreferrer'
                >
                  {item?.label}
                </a>
              );
            } else {
              return (
                <a
                  key={sys?.id}
                  href='#'
                  id='ot-sdk-link'
                  onClick={() => {
                    OneTrust.ToggleInfoDisplay();
                  }}
                  aria-label={item?.ariaLabel}
                  className={`${footerNavItem} ot-sdk-show-settings`}
                >
                  {item?.label}
                </a>
              );
            }
          })}
        </div>
      </div>
    </footer>
  );
};

Footer.PropTypes = {
  primaryNavFooterData: PropTypes.array.isRequired,
  secondaryNavFooterData: PropTypes.array.isRequired,
  copyright: PropTypes.string.isRequired
};

export default memo(Footer);
